import React, { useCallback } from 'react';
import { chunk, isEqual, shuffle, times } from 'lodash';

import SectionDividers from '@dyl/components/SectionDividers';
import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import RubricBlock from '@dyl/blocks/RubricBlock';
import { Grid, IconButton, Tooltip, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ConnectingLinesRenderer } from '@dyl/components/ConnectingLines';
import { Restore as ResetIcon, Shuffle as ShuffleIcon } from '@mui/icons-material';

import Stimulus from './Stimulus';
import { deriveDisplayOrder, useMicroCheck } from './utils';
import MicroItem from './MicroItem';
import Answer from './Answer';

const useStyles = makeStyles(theme => ({
    root: {
        maxWidth: theme.breakpoints.values.md,
    },
}));

export default function FactualTopicEditor() {
    const classes = useStyles();
    const [items, setItems] = useTaskField('items');

    const sections = chunk(items, 4);

    const showMicroVersion = useMicroCheck();

    const resetOrder = useCallback(sectionIndex => {
        setItems(prev => prev.map((item, index) => {
            const start = sectionIndex * 4;
            if (index < start || index > start + 3) {
                return item;
            }

            return {
                ...item,
                answers: item.answers.map(answer => ({
                    ...answer,
                    display_order: (index % 4) + 1,
                })),
            };
        }));
    }, []);

    const shuffleOrder = useCallback(sectionIndex => {
        setItems(prev => {
            const prevOrder = deriveDisplayOrder(chunk(prev, 4)[sectionIndex]);

            /* Attempt up to 100 times to get a different order to last time.
             * Reduces chance of same order from 1/24 to (1/24)^100. */
            const order = (() => {
                let order = [];

                for (let attempt = 0; attempt < 100; attempt++) {
                    order = shuffle(times(4, i => i + 1));

                    if (!isEqual(order, prevOrder)) {
                        break;
                    }
                }

                return order;
            })();

            return prev.map((item, index) => {
                const start = sectionIndex * 4;
                if (index < start || index > start + 3) {
                    return item;
                }

                return {
                    ...item,
                    answers: item.answers.map(answer => ({
                        ...answer,
                        display_order: order[index % 4],
                    })),
                };
            });
        });
    }, []);

    return (
        <div className={classes.root}>
            <ConnectingLinesRenderer>
                <SectionDividers>
                    <RubricBlock />

                    {sections.map((section, sectionIndex) => {
                        const answerDisplayOrder = deriveDisplayOrder(section);

                        return (
                            <React.Fragment key={sectionIndex}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={5}>
                                        <Typography variant='h6'>
                                            Phase {sectionIndex + 1}
                                        </Typography>
                                    </Grid>

                                    {/* Can only edit display order in screen sizes above XS */}
                                    {!showMicroVersion && (
                                        <>
                                            <Grid item xs={2} />

                                            <Grid item xs={5} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                                                <div  style={{ flex: 1 }}>
                                                    <Typography variant='subtitle1'>
                                                        Answers
                                                    </Typography>
                                                    <Typography variant='caption'>
                                                        Drag the handles to change display order.
                                                    </Typography>
                                                </div>

                                                {/* Buttons */}
                                                <Tooltip title='Reset Order'>
                                                    <IconButton onClick={() => resetOrder(sectionIndex)} tabIndex={-1} size="large">
                                                        <ResetIcon />
                                                    </IconButton>
                                                </Tooltip>

                                                <Tooltip title='Shuffle Order'>
                                                    <IconButton onClick={() => shuffleOrder(sectionIndex)} tabIndex={-1} size="large">
                                                        <ShuffleIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Grid>
                                        </>
                                    )}
                                </Grid>

                                <Grid container spacing={2}>
                                    {/* On XS screens, a smaller, vertical layout is used. */}
                                    {showMicroVersion ? times(section.length, subIndex => {
                                        const i = sectionIndex * 4 + subIndex;

                                        return <MicroItem key={i} index={i} />
                                    }) : (
                                        // On wider-than-xs screens, the full layout is used.
                                        <>
                                            {/* Stimulus column */}
                                            <Grid item xs={5}>
                                                {times(section.length, subIndex => {
                                                    const i = sectionIndex * 4 + subIndex;

                                                    return <Stimulus key={i} index={i} />;
                                                })}
                                            </Grid>

                                            <Grid item xs={2} />

                                            <Grid item xs={5}>
                                                {answerDisplayOrder.map(subIndex => {
                                                    const i = sectionIndex * 4 + subIndex;

                                                    return <Answer key={i} index={i} />;
                                                })}
                                            </Grid>
                                        </>
                                    )}
                                </Grid>
                            </React.Fragment>
                        );
                    })}
                </SectionDividers>
            </ConnectingLinesRenderer>
        </div>
    );
}